import { Avatar } from '../ui/Avatar.tsx';
import { BUCKET_LABELS, bucketOf, type DayBucket, dueLabel } from './dates.ts';
import type { Person, Task } from './source.ts';

/** The round box that completes a task. A real checkbox to a reader of the page. */
export function Tick({
  task,
  onToggle,
}: {
  task: Pick<Task, 'title' | 'done'>;
  onToggle: (done: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      className="tasks-tick"
      checked={task.done}
      aria-label={`${task.title}, done`}
      onChange={(e) => onToggle(e.target.checked)}
    />
  );
}

export function Who({ person, size }: { person: Person | null; size?: 'sm' }) {
  if (!person) return null;
  return <Avatar name={person.name} seed={person.id} {...(size ? { size } : {})} />;
}

/** One task as a row: tick, title over where it came from, who, and when. */
export function TaskRow({
  task,
  today,
  showWho,
  selected,
  onOpen,
  onToggle,
}: {
  task: Task;
  today: string;
  showWho: boolean;
  selected: boolean;
  onOpen: () => void;
  onToggle: (done: boolean) => void;
}) {
  const overdue = !task.done && task.dueOn !== null && task.dueOn < today;
  return (
    <li
      className="tasks-row"
      data-selected={selected || undefined}
      data-done={task.done || undefined}
    >
      <Tick task={task} onToggle={onToggle} />
      <button type="button" className="tasks-row-open" onClick={onOpen}>
        <span className="tasks-row-title">{task.title}</span>
        {task.from && <span className="tasks-row-from">{task.from.label}</span>}
      </button>
      {showWho && <Who person={task.assignee} size="sm" />}
      {task.dueOn && (
        <span className="tasks-row-when" data-overdue={overdue || undefined}>
          {dueLabel(task.dueOn, today)}
        </span>
      )}
    </li>
  );
}

export type Group = { key: string; label: string; tasks: readonly Task[] };

const ORDER: readonly DayBucket[] = ['overdue', 'today', 'week', 'later', 'none'];

/** Open tasks in day groups, then the done ones, each group in the order it was given. */
export function groupByDay(tasks: readonly Task[], today: string): Group[] {
  const groups: Group[] = [];
  for (const bucket of ORDER) {
    const inside = tasks
      .filter((t) => !t.done && bucketOf(t.dueOn, today) === bucket)
      .sort((a, b) => (a.dueOn ?? '9').localeCompare(b.dueOn ?? '9'));
    if (inside.length) groups.push({ key: bucket, label: BUCKET_LABELS[bucket], tasks: inside });
  }
  const done = tasks.filter((t) => t.done);
  if (done.length) groups.push({ key: 'done', label: 'Done', tasks: done });
  return groups;
}

export function GroupedRows({
  groups,
  today,
  showWho,
  openId,
  onOpen,
  onToggle,
}: {
  groups: readonly Group[];
  today: string;
  showWho: boolean;
  openId: string | null;
  onOpen: (task: Task) => void;
  onToggle: (task: Task, done: boolean) => void;
}) {
  return (
    <>
      {groups.map((group) => (
        <section key={group.key} className="tasks-group" aria-label={group.label}>
          <h3 className="tasks-group-head">
            {group.label}
            <span className="tasks-count">{group.tasks.length}</span>
          </h3>
          <ul className="tasks-rows">
            {group.tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                today={today}
                showWho={showWho}
                selected={task.id === openId}
                onOpen={() => onOpen(task)}
                onToggle={(done) => onToggle(task, done)}
              />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
